import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Lightweight tooltip implementation that avoids the Radix UI TooltipProvider
 * hook crash caused by duplicate React instances in the Vite dev cache.
 */

type TooltipProviderProps = React.PropsWithChildren<{
  delayDuration?: number;
  skipDelayDuration?: number;
  disableHoverableContent?: boolean;
}>;

const TooltipProvider = ({ children }: TooltipProviderProps) => <>{children}</>;

const Tooltip = ({ children }: { children: React.ReactNode }) => (
  <span style={{ position: "relative", display: "inline-flex" }}>{children}</span>
);

const TooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ children, ...props }, ref) => {
  if (props.asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, { ref });
  }
  return <button ref={ref} type="button" {...props}>{children}</button>;
});
TooltipTrigger.displayName = "TooltipTrigger";

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { side?: string; sideOffset?: number; align?: string }
>(({ className, children, side, sideOffset, align, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
